/**
 * Sharing a cell as a picture (brief §17.2).
 *
 * **An image, never a link.** There is no public URL to a cell and there is not going to be
 * one: the instance is invite-only and admin-approved, so a shareable link would be either
 * useless to the recipient or a hole in the gate. A PNG says "look what it was like here" and
 * grants nothing.
 *
 * Rendered on a canvas in the browser. No server rendering, which would mean the server
 * drawing a map of a place — and the server has a cell index and no map.
 */

import type { Attribute } from '$domain/attributes.ts';
import { gdiColour, gdiLabel, vibeSymbol } from './gdi-scale.ts';
import wordmarkUrl from './wordmark.webp';

const WIDTH = 1080;
const HEIGHT = 1080;
const MARGIN = 72;
const INNER = WIDTH - MARGIN * 2;

export type ShareCard = {
  gdi: number;
  vibe: number | null;
  sightingCount: number;
  facets: Record<string, number>;
  topAttribute: string | null;
  /** Mean per star attribute, for the bars. */
  bars: { attribute: Attribute; value: number }[];
  place: string | undefined;
};

/**
 * The wordmark, decoded once and kept.
 *
 * Same-origin, so drawing it does **not** taint the canvas and `toBlob` still works — which is
 * the whole reason it comes through the bundler rather than from anywhere else. A failure
 * resolves to `undefined` rather than throwing, and the caller falls back to the name in text:
 * a share card without the logo is still a share card, a card that never renders is not.
 */
let wordmark: Promise<HTMLImageElement | undefined> | undefined;

export function loadWordmark(): Promise<HTMLImageElement | undefined> {
  wordmark ??= new Promise<HTMLImageElement | undefined>((resolve) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(undefined);
    image.src = wordmarkUrl;
  });
  return wordmark;
}

/**
 * Draw `text`, shrinking the font until it fits and giving up with an ellipsis.
 *
 * A place name is whatever Nominatim returns — "Königstraße, Mitte, Stuttgart" is normal and
 * something twice that long is not unusual. Without this it runs off the edge of the picture,
 * which is the sort of thing nobody notices until it is on somebody else's phone.
 */
export function fitted(
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  weight: number,
  size: number,
): void {
  let px = size;
  context.font = `${weight} ${px}px system-ui, sans-serif`;
  while (context.measureText(text).width > maxWidth && px > size * 0.7) {
    px -= 2;
    context.font = `${weight} ${px}px system-ui, sans-serif`;
  }
  let shown = text;
  while (context.measureText(shown).width > maxWidth && shown.length > 1) {
    shown = shown.slice(0, -2);
  }
  context.fillText(shown === text ? text : `${shown}…`, x, y);
}

export function rounded(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  context.beginPath();
  context.roundRect(x, y, width, height, radius);
}

/**
 * Draw the card. Returns the canvas so the caller can decide what to do with it.
 *
 * Laid out with a running cursor rather than fixed baselines: the place name is optional and
 * the number of bars is not known until the data is in, so any hardcoded offset would be right
 * for exactly one of the shapes this card takes.
 */
export async function drawShareCard(card: ShareCard): Promise<HTMLCanvasElement> {
  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const context = canvas.getContext('2d');
  if (context === null) return canvas;

  const accent = gdiColour(card.gdi);

  context.fillStyle = '#12141a';
  context.fillRect(0, 0, WIDTH, HEIGHT);

  // A wash of the GDI colour, so the picture reads at thumbnail size before any text does.
  const wash = context.createLinearGradient(0, 0, 0, HEIGHT);
  wash.addColorStop(0, `${accent}55`);
  wash.addColorStop(1, '#12141a00');
  context.fillStyle = wash;
  context.fillRect(0, 0, WIDTH, HEIGHT);

  let y = MARGIN;

  const logo = await loadWordmark();
  if (logo === undefined) {
    context.fillStyle = '#ffffff';
    context.font = '600 48px system-ui, sans-serif';
    context.fillText('hotspotter', MARGIN, y + 48);
    y += 84;
  } else {
    const width = 400;
    const height = (logo.naturalHeight / logo.naturalWidth) * width;
    context.drawImage(logo, MARGIN, y, width, height);
    y += height + 36;
  }

  context.fillStyle = accent;
  context.font = '700 260px system-ui, sans-serif';
  y += 190;
  context.fillText(String(card.gdi), MARGIN, y);

  context.fillStyle = '#ffffff';
  context.font = '500 56px system-ui, sans-serif';
  y += 74;
  context.fillText(gdiLabel(card.gdi), MARGIN, y);

  // The place on its own line, because "where was this?" is the first thing anybody receiving
  // the picture asks, and it does not deserve to be crammed in behind a middot.
  if (card.place !== undefined && card.place !== '') {
    context.fillStyle = '#d5d9e0';
    y += 64;
    fitted(context, `📍 ${card.place}`, MARGIN, y, INNER, 400, 42);
  }

  context.fillStyle = '#9aa0aa';
  context.font = '400 34px system-ui, sans-serif';
  y += 52;
  const counts = [
    `${card.sightingCount} Meldungen`,
    card.vibe === null ? undefined : `${vibeSymbol(card.vibe)} ${card.vibe}`,
  ]
    .filter((part) => part !== undefined)
    .join(' · ');
  context.fillText(counts, MARGIN, y);

  // The three strongest attributes as bars. More than three and it stops being a glance.
  const bars = [...card.bars].sort((a, b) => b.value - a.value).slice(0, 3);
  y += 78;
  for (const bar of bars) {
    context.fillStyle = '#ffffff';
    context.font = '400 38px system-ui, sans-serif';
    context.fillText(`${bar.attribute.emoji} ${bar.attribute.label}`, MARGIN, y);

    context.fillStyle = '#ffffff22';
    rounded(context, MARGIN, y + 20, INNER, 28, 14);
    context.fill();

    context.fillStyle = accent;
    rounded(context, MARGIN, y + 20, (INNER * bar.value) / 5, 28, 14);
    context.fill();
    y += 112;
  }

  context.fillStyle = '#6b7280';
  context.font = '400 30px system-ui, sans-serif';
  context.fillText('Geschicktendichte-Index · hotspotter', MARGIN, HEIGHT - MARGIN);

  return canvas;
}

export type ShareOutcome = 'shared' | 'copied' | 'unsupported';

/**
 * Hand the picture to the platform.
 *
 * `navigator.share` where it exists, the clipboard where it does not. Deliberately **not** a
 * download link: a viewer sandbox blocks page-initiated downloads, and on a phone a file in
 * the download folder is not what anybody meant by "share this".
 */
export async function shareCard(
  card: ShareCard,
  filename = 'hotspotter.png',
): Promise<ShareOutcome> {
  return sharePicture(await drawShareCard(card), filename);
}

/** Any picture drawn here, handed over the same way — a cell's card or a month's (amendment 26). */
export async function sharePicture(
  canvas: HTMLCanvasElement,
  filename: string,
): Promise<ShareOutcome> {
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (blob === null) return 'unsupported';

  const file = new File([blob], filename, { type: 'image/png' });
  if (navigator.canShare?.({ files: [file] }) === true) {
    try {
      await navigator.share({ files: [file], title: 'hotspotter' });
      return 'shared';
    } catch {
      // Cancelled, which is not a failure worth reporting as one.
      return 'shared';
    }
  }

  try {
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
    return 'copied';
  } catch {
    return 'unsupported';
  }
}
